import React from "react";
import {
  ArrowRight,
  Check,
} from "lucide-react";
import { NavLink, useLocation } from "react-router-dom";
import Stepper, { Step } from "../components/Stepper";
import { useBookAssessment } from "../hooks/useBookAssessment";
import Seo from "../components/Seo";
import Breadcrumbs from "../components/Breadcrumbs";
import InfrastructureExamples from "../components/InfrastructureExamples";
import ArchitectureDiagram from "../components/ArchitectureDiagram";
import { buildPersonSchema, buildServiceSchema, type Crumb } from "../utils/structuredData";
import { coreServices, successProcess } from "../content/services";
import { advisoryBoard, executiveTeam, initials, whoWeServe } from "../content/about";

const processPhases = [
  {
    label: "Understand",
    range: "Steps 1-2",
    description: "Find the real bottlenecks and prioritize the work.",
  },
  {
    label: "Build",
    range: "Steps 3-4",
    description: "Design, connect, and prove the right solution.",
  },
  {
    label: "Operate",
    range: "Steps 5-6",
    description: "Launch securely, measure impact, and keep improving.",
  },
];

const processOutcomes = [
  "A clear workflow map",
  "A prioritized automation roadmap",
  "A solution blueprint built around your stack",
  "Working automation connected to real systems",
  "A secure, phased rollout",
  "Ongoing optimization and support",
];

const About: React.FC = () => {
  const location = useLocation();
  const bookAssessment = useBookAssessment();

  const seoTitle = "About Contextral | Private AI and Automation";
  const seoDescription = "Contextral builds secure AI workflow automation into the systems you already run — no rip-and-replace. Explore our services and book a free automation assessment.";
  const serviceSchema = buildServiceSchema(
    location.pathname,
    "AI Automation That Fits How You Already Work",
    "We install a private, secure automation layer between the systems you already run and where your documents and data live — connecting your platforms, automating the work in between, and routing results into secure storage you control. No costly overhaul, no rip-and-replace.",
  );

  const teamSchemas = [...executiveTeam, ...advisoryBoard].map((person) =>
    buildPersonSchema(person.name, person.role, person.credential));

  const crumbs: Crumb[] = [
    { name: "Home", path: "/" },
    { name: "About", path: location.pathname },
  ];

  return (
    <div className="relative pt-6">
      <Seo title={seoTitle} description={seoDescription} jsonLd={[serviceSchema, ...teamSchemas]} />

      <section>
        <Breadcrumbs items={crumbs} />
        <div className="text-center mb-10 sm:mb-14">
          <h1 className="font-bold text-text-primary mb-4 text-3xl sm:text-4xl lg:text-5xl leading-tight">
            AI Automation Between Your Systems and Data
          </h1>
          <p className="text-base sm:text-lg md:text-xl text-text-secondary max-w-4xl mx-auto leading-relaxed">
            We build a private, secure automation layer between the systems you run and the data you own, without any costly overhaul.
          </p>
        </div>
      </section>

      <ArchitectureDiagram />

      <section className="relative max-w-full overflow-hidden bg-surfaceSoft py-12 sm:py-16 md:py-20 mb-0">
        <div className="pointer-events-none absolute inset-0" aria-hidden="true">
          <div className="absolute -top-24 -left-24 h-80 w-80 rounded-full bg-primary/10 blur-3xl" />
          <div className="absolute -bottom-24 -right-16 h-96 w-96 rounded-full bg-accentPurple/10 blur-3xl" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8 sm:mb-10">
          <h2 className="text-3xl sm:text-4xl font-bold text-text-primary mb-3">Our Core Services</h2>
          <p className="mx-auto max-w-3xl text-base sm:text-lg text-text-secondary leading-relaxed">
            Six capabilities we combine into one secure automation layer, shaped to how your team already works.
          </p>
        </div>

        {/* Bento grid: the two flagship services (Custom Cloud Workflows, Private AI
          Deployments) take a wider cell so the eye has anchors instead of a
            uniform 3×2 wall. Spans tile a perfect 4×2 grid on large screens. */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5 lg:auto-rows-fr">
          {coreServices.map((service, index) => {
            const featured = index === 0 || index === 1;
            return (
              <div
                key={service.title}
                className={`group relative overflow-hidden rounded-2xl border border-borderSubtle p-6 sm:p-7 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-primary/40 hover:shadow-xl hover:shadow-primary/10 ${
                  featured ? "lg:col-span-2 bg-surface" : "bg-surface"
                }`}
              >
                {featured && (
                  <div className="pointer-events-none absolute -right-10 -top-10 h-32 w-32 rounded-full bg-accentPurple/10 blur-2xl" aria-hidden="true" />
                )}
                <div className="relative">
                  <div className="w-12 h-12 rounded-xl bg-primary text-white flex items-center justify-center mb-4 shadow-lg shadow-accentPurple/20">
                    <service.icon className="w-6 h-6" />
                  </div>
                  <h3 className="text-lg sm:text-xl font-bold text-text-primary mb-2 transition-colors duration-300 group-hover:text-primary">{service.title}</h3>
                  <p className="text-text-secondary leading-relaxed text-sm sm:text-base">{service.description}</p>
                </div>
              </div>
            );
          })}
        </div>
        </div>
      </section>

      <InfrastructureExamples />

      <section className="relative max-w-full overflow-hidden bg-surfaceSoft py-12 sm:py-16 md:py-20 mb-0">
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8 sm:mb-10">
          <h2 className="text-3xl sm:text-4xl font-bold text-text-primary mb-3">Our Customer Success Process</h2>
          <p className="mx-auto max-w-3xl text-base sm:text-lg text-text-secondary leading-relaxed">
            A practical path from understanding how your team works to operating secure automation that keeps getting better.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-5">
          {processPhases.map((phase, index) => {
            const PhaseIcon = successProcess[index * 2].icon;
            return (
              <div key={phase.label} className="group rounded-2xl border border-borderSubtle bg-surface p-4 sm:p-5 transition-all duration-300 hover:-translate-y-1 hover:border-primary/30 hover:shadow-lg hover:shadow-primary/10">
                <div className="flex items-start gap-4">
                  <div className="inline-flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-primary text-white shadow-md shadow-primary/20">
                    <PhaseIcon className="h-5 w-5" aria-hidden="true" />
                  </div>
                  <div>
                    <p className="text-xs font-bold uppercase tracking-[0.16em] text-primary">{phase.range}</p>
                    <h3 className="text-lg font-bold text-text-primary mt-1">{phase.label}</h3>
                    <p className="text-sm text-text-secondary leading-relaxed mt-1">{phase.description}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mx-auto relative overflow-hidden rounded-3xl border border-primary/15 bg-gradient-to-br from-surface via-surface to-primary/5 shadow-xl shadow-primary/10 p-4 sm:p-7">
          <Stepper
            initialStep={1}
            onStepChange={() => {}}
            onFinalStepCompleted={() => bookAssessment()}
            backButtonText="Previous"
            nextButtonText="Next step"
            finalButtonText="Book an assessment"
            stepCircleContainerClassName="p-0"
            stepContainerClassName="px-1 sm:px-3 pb-6 pt-2 overflow-x-auto"
            contentClassName="px-1 sm:px-3"
            footerClassName="px-1 sm:px-3 pb-1"
            backButtonProps={{
              className:
                "inline-flex items-center justify-center rounded-full border border-borderSubtle bg-surfaceSoft px-5 py-2.5 text-sm font-medium text-text-secondary hover:text-text-primary hover:border-primary/40 transition-colors duration-200",
            }}
            nextButtonProps={{
              className:
                "inline-flex items-center justify-center rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-text-inverse hover:bg-primaryHover transition-colors duration-200",
            }}
            renderStepIndicator={({ step, currentStep, onStepClick }) => {
              const isComplete = currentStep > step;
              const isActive = currentStep === step;
              const processStep = successProcess[step - 1];
              const stepTitle = processStep.title;
              const IndicatorIcon = processStep.icon;

              return (
                <button
                  type="button"
                  onClick={() => onStepClick(step)}
                  className="group inline-flex min-w-[42px] flex-col items-center gap-2 transition-all duration-200 sm:min-w-[72px]"
                  aria-label={`Go to step ${step}: ${stepTitle}`}
                >
                  <span className={`inline-flex h-11 w-11 items-center justify-center rounded-xl border text-sm font-semibold transition-all duration-300 ${
                    isActive
                      ? "scale-110 border-primary bg-primary text-white shadow-lg shadow-primary/30"
                      : isComplete
                        ? "border-primary/40 bg-primary/15 text-primary"
                        : "border-borderSubtle bg-surface text-text-muted group-hover:border-primary/40 group-hover:text-primary"
                  }`}>
                    {isComplete ? <Check className="h-5 w-5" aria-hidden="true" /> : <IndicatorIcon className="h-5 w-5" aria-hidden="true" />}
                  </span>
                  <span className={`hidden sm:block text-xs font-semibold max-w-[92px] text-center leading-tight ${
                    isActive ? "text-primary" : isComplete ? "text-primary/70" : "text-text-muted"
                  }`}>
                    {stepTitle}
                  </span>
                </button>
              );
            }}
          >
            {successProcess.map((processStep) => (
              <Step key={processStep.title}>
                <div className="grid items-center gap-5 py-4 sm:py-7 md:grid-cols-[112px_minmax(0,1fr)_240px] md:gap-8">
                  <div className="relative mx-auto flex h-24 w-24 items-center justify-center rounded-3xl bg-primary text-white shadow-xl shadow-primary/25 md:mx-0">
                    <processStep.icon className="h-10 w-10" aria-hidden="true" />
                    <span className="absolute -right-2 -top-2 inline-flex h-8 w-8 items-center justify-center rounded-full border-4 border-surface bg-accentPink text-xs font-bold text-white">
                      {processStep.step}
                    </span>
                  </div>

                  <div>
                    <p className="text-xs font-bold uppercase tracking-[0.18em] text-primary mb-2">
                      Step {processStep.step} of {successProcess.length}
                    </p>
                    <h3 className="text-2xl sm:text-3xl font-bold text-text-primary mb-3">{processStep.title}</h3>
                    <p className="text-text-secondary text-base sm:text-lg leading-relaxed">{processStep.description}</p>
                  </div>

                  <div className="rounded-2xl border border-primary/15 bg-surface/90 p-5 shadow-sm backdrop-blur-sm">
                    <div className="flex items-center gap-2 text-primary mb-3">
                      <span className="text-xs font-bold uppercase tracking-[0.16em]">What you get</span>
                    </div>
                    <p className="font-semibold text-text-primary leading-relaxed">
                      {processOutcomes[processStep.step - 1]}
                    </p>
                  </div>
                </div>
              </Step>
            ))}
          </Stepper>
        </div>
        </div>
      </section>

      <section aria-labelledby="who-we-serve-title" className="py-12 sm:py-16">
        <div className="text-center mb-10 sm:mb-12">
          <h2 id="who-we-serve-title" className="text-3xl sm:text-4xl font-bold text-text-primary mb-3">Who We Serve</h2>
          <p className="mx-auto max-w-3xl text-base sm:text-lg text-text-secondary leading-relaxed">
            Anywhere sensitive records live and the data can't leave the building.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 sm:gap-6">
          {whoWeServe.map((group) => (
            <div key={group.title} className="flex flex-col rounded-2xl border border-borderSubtle bg-surface p-6 sm:p-8 shadow-sm">
              <group.icon className="mb-4 h-9 w-9 text-primary" aria-hidden="true" />
              <h3 className="text-xl font-bold text-text-primary mb-3">{group.title}</h3>
              <ul className="space-y-2.5">
                {group.points.map((point) => (
                  <li key={point} className="flex items-start gap-2.5 text-text-secondary">
                    <Check className="mt-1 h-4 w-4 flex-none text-primary" aria-hidden="true" />{point}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      <section aria-labelledby="team-title" className="py-12 sm:py-16">
        <div className="text-center mb-10 sm:mb-14">
          <h2 id="team-title" className="text-3xl sm:text-4xl font-bold text-text-primary mb-3">Leadership & Advisory Board</h2>
          <p className="mx-auto max-w-4xl text-base sm:text-lg text-text-secondary leading-relaxed">
            Meet the people behind Contextral and the experts guiding our journey.
          </p>
        </div>
        {[{ title: "Leadership", people: executiveTeam }, { title: "Advisory Board", people: advisoryBoard }].map((group) => (
          <div key={group.title} className="mb-10 last:mb-0">
            <h3 className="mb-5 text-center text-xl font-bold text-text-primary">{group.title}</h3>
            <div className="flex flex-wrap justify-center gap-5 sm:gap-6">
              {group.people.map((person) => (
                <div key={person.name}
                     className="w-full rounded-2xl border border-borderSubtle bg-surface p-6 text-center shadow-sm transition-all duration-300 hover:border-primary/40 hover:shadow-lg hover:shadow-primary/10 sm:w-[calc(50%-0.75rem)] lg:w-[calc(20%-1.2rem)] lg:min-w-[200px]">
                  <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full border border-primary/20 bg-gradient-to-br from-primary/15 to-accentPurple/20 text-lg font-bold text-primary">
                    {initials(person.name)}
                  </div>
                  <h4 className="text-base font-bold text-text-primary">{person.name}</h4>
                  <p className="mt-1 text-sm font-medium italic text-primary">{person.role}</p>
                  <p className="mt-2 text-sm text-text-secondary leading-relaxed">{person.credential}</p>
                </div>
              ))}
            </div>
          </div>
        ))}
      </section>

      <section className="relative z-10 pb-12 sm:pb-16 md:pb-20 bg-transparent mb-0 max-w-full">
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="relative overflow-hidden bg-primary rounded-2xl p-6 sm:p-8 md:p-12 text-center shadow-xl shadow-primary/25">
          <div className="pointer-events-none absolute inset-0 bg-blueprint opacity-30" aria-hidden="true" />
          <h2 className="relative text-2xl sm:text-3xl md:text-4xl font-bold text-text-inverse mb-3 sm:mb-4">
            Start With a Free Automation Assessment
          </h2>
          <p className="relative text-surfaceSoft text-base sm:text-lg md:text-xl max-w-3xl mx-auto mb-7">
            We'll map where automation saves you the most time and risk, tailored to your organization.
          </p>
          <div className="relative flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
            <NavLink
              to="/contact"
              className="group w-full sm:w-auto inline-flex items-center justify-center bg-surface text-primary px-6 sm:px-8 py-3 sm:py-4 rounded-full text-base sm:text-lg font-semibold hover:scale-105 transform transition-all duration-300 shadow-lg"
            >
              Contact Our Team
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </NavLink>
            <button
              type="button"
              onClick={() => bookAssessment()}
              className="group w-full sm:w-auto inline-flex items-center justify-center border border-white/60 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-full text-base sm:text-lg font-semibold hover:bg-white/10 transition-all duration-300"
            >
              Book a Free Automation Assessment
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
        </div>
        </div>
      </section>
    </div>
  );
};

export default About;
